/** @jsx h */
/** @jsxFrag Fragment */
import { Fragment, h } from "preact";
import { useEffect } from "preact/hooks";

import { ParamDictKeyOnChange } from "@utils/params";
import { Param, ParamDict } from "@utils/params";
import { pathToObject } from "@utils/utils";
import { DatBoolean } from "./datBoolean.tsx";
import { DatSubFolder } from "./datFolder.tsx";
import { DatNumber } from "./datNumber.tsx";
import { DatSelect } from "./datSelect.tsx";
import { DatString } from "./datString.tsx";
import { DatColor } from "./datColor.tsx";
import { hslaToHsva, hsvaToHsla } from "@utils/color";
import { FoldersContext, useDatFolderDispatch } from "./dat.useFolderDispatch.tsx";

export interface DatParamProps {
  param: Param;
  label: string;
  onChange: (value: any) => void;
}
export function DatParam(props: DatParamProps) {
  const param = props.param;
  const k = props.label;
  if (param.type === "string") {
    return <DatString label={k} onChange={props.onChange} value={param.value} />;
  } else if (param.type === "boolean") {
    return <DatBoolean label={k} onChange={props.onChange} value={param.value} />;
  } else if (param.type === "list") {
    return (
      <DatSelect
        label={k}
        value={param.value}
        options={param.list}
        optionLabels={param.list}
        onChange={props.onChange}
      />
    );
  } else if (param.type === "float" || param.type === "int") {
    return (
      <DatNumber
        label={k}
        value={param.value}
        min={param.min || 0}
        max={param.max || 1}
        step={param.step}
        onChange={props.onChange}
      />
    );
  } else if (param.type === "hsla") {
    return (
      <DatColor
        label={k}
        onChange={(c) => {
          props.onChange(hsvaToHsla({ ...hslaToHsva(param.value), ...c }));
        }}
        value={hslaToHsva(param.value)}
      />
    );
  } else {
    return null;
  }
}

export interface DatParamFolderProps<T extends ParamDict> {
  label: string;
  path: string;
  children: object;
  params: T;
  onChange: ParamDictKeyOnChange<T>;
  folders: FoldersContext;
}
export function DatParamFolder<T extends ParamDict>(props: DatParamFolderProps<T>) {
  const children = Object.entries(props.children).map(([k, v]) => {
    if (typeof v === "string") {
      return (
        <DatParam
          key={k}
          label={k}
          param={props.params[v]}
          onChange={(value) => props.onChange(v, value)}
        />
      );
    } else {
      return (
        <DatParamFolder
          key={k}
          label={k}
          path={[props.path, k].join("/")}
          // deno-lint-ignore jsx-no-children-prop
          children={v}
          params={props.params}
          onChange={props.onChange}
          folders={props.folders}
        />
      );
    }
  });

  return (
    <DatSubFolder path={props.path} title={props.label} folders={props.folders}>
      {children}
    </DatSubFolder>
  );
}

export interface DatParamsOpts {
  "folders/defaultOpen": boolean;
}
export const DatParamsOptsDefault: DatParamsOpts = {
  "folders/defaultOpen": false,
};

export interface DatParamsProps<T extends ParamDict> {
  params: T;
  onChange: ParamDictKeyOnChange<T>;
  folders?: FoldersContext;

  path?: string;
  label?: string;
  opts?: Partial<DatParamsOpts>;
}
export function DatParams<T extends ParamDict>(props: DatParamsProps<T>) {
  const label = props.label || "root";
  const paramKeys = Object.keys(props.params);
  const grouped = pathToObject(paramKeys);

  const folders = props.folders || useDatFolderDispatch();

  useEffect(() => {}, [folders]);

  return (
    <DatParamFolder
      label={label}
      path={props.path || ""}
      params={props.params}
      onChange={props.onChange}
      folders={folders}
    >
      {grouped}
    </DatParamFolder>
  );
}

export interface DatParamsFlatProps<T extends ParamDict> {
  params: T;
  onChange: ParamDictKeyOnChange<T>;
}
export function DatParamsFlat<T extends ParamDict>(props: DatParamsFlatProps<T>) {
  const res = Object.entries(props.params).map(([path, param]) => {
    return (
      <DatParam
        key={path}
        label={path}
        param={param}
        onChange={(value) => props.onChange(path, value)}
      />
    );
  });
  return res;
}
